import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { RootState } from '../store';
import { FontFamilyKey, FontWeightKey } from '../../themes/fontConfig';

export type AnaAvatarStyle = 'nebula' | 'aurora' | 'prism' | 'pulse' | 'ember';

/** Floating Ana assistant button presentation: 'modern' = avatar speed dial, 'classic' = two-button bar. */
export type AnaFabStyle = 'modern' | 'classic';

/** Platform-wide upload ceiling, in whole megabytes. Mirrors the backend bounds. */
export const DEFAULT_MAX_UPLOAD_SIZE_MB = 6;
export const MIN_MAX_UPLOAD_SIZE_MB = 1;
export const MAX_MAX_UPLOAD_SIZE_MB = 100;

/**
 * The free tier is app-admin configuration rather than a product record, because every
 * account is enrolled on it at sign-up and it must never reach the Stripe checkout.
 * These defaults mirror `freeTierConfig.js` in the stripe layer and are what the backend
 * grants when nothing has been configured.
 */
export interface FreeTierGrant {
	type: string;
	value: number | string;
	slug?: string;
}

export interface FreeTierSettings {
	name: string;
	description: string;
	grants: FreeTierGrant[];
}

export const FREE_TIER_DEFAULTS: FreeTierSettings = {
	name: 'Pearsana Explorer',
	description: 'Create your free wallet, upload documents, and explore universities in the Pearsana portal!',
	grants: [
		{ type: 'walletStorage', value: 5 },
		{ type: 'tokenBudget', value: 10000 },
		{ type: 'tokenDailyBudget', value: 2000 },
		{ type: 'feature', value: 'portal' }
	]
};

/** Per-grant ceilings. Token budgets are counted in tokens, so they sit on a far larger scale. */
export const FREE_TIER_NUMERIC_GRANT_LIMITS: Record<string, number> = {
	walletStorage: 10000,
	verificationRequests: 10000,
	candidateEnquiries: 10000,
	advisorSession: 1000,
	tokenBudget: 10000000,
	tokenDailyBudget: 10000000
};

/** Grants whose value is a label rather than a count. */
export const FREE_TIER_STRING_GRANT_TYPES = ['feature', 'resourceAccess', 'resourceCategory'];

export const MAX_FREE_TIER_GRANTS = 40;
export const MAX_FREE_TIER_NAME_LENGTH = 30;
export const MAX_FREE_TIER_DESCRIPTION_LENGTH = 500;

/** A partner organisation that has claimed a namespace, so it can be delivered through. */
export interface DeliveryPartnerOption {
	namespace: string;
	organisationId: string;
	organisationName: string;
}

/** A destination whose contacts are the team's own addresses, for safe end-to-end testing. */
export interface TestDestination {
	displayName: string;
	institutionName?: string;
	departmentName?: string;
	pearsanaDelivery?: boolean;
	contacts: { contactName?: string; emailAddress: string }[];
}

/** Operational view of the cached ECE destination network, for the admin panel. */
export interface DestinationCacheStatus {
	fetchedAt: string | null;
	count: number;
	lastError: string | null;
	lastErrorAt: string | null;
}

/** The secret is described, never returned — see describeEceConfig in ssharing. */
export interface EceConfig {
	baseUrl: string;
	baseUrlUpdatedAt: string | null;
	secretSet: boolean;
	secretLength: number;
	secretLooksQuoted: boolean;
	secretWasTrimmed: boolean;
	secretUpdatedAt: string | null;
	/** v2 retrieval settings (PTEC-896). Null where the function predates them. */
	shareFile?: EceShareFileConfig | null;
}

/** Where institution shares fetch ECE's v2 of a report from. Authenticated with the ECE secret. */
export interface EceShareFileConfig {
	/** Only needed if ECE send a bare file id instead of the v2 URL. */
	retrievalPath: string;
	metadataKey: string;
	maxMb: number;
	/** Hosts a v2 URL may be on besides the ECE API host, which is always allowed. */
	allowedHosts: string[];
}

export interface EceConnectionTest {
	ok: boolean;
	destinationCount?: number;
	status?: number | null;
	error?: string;
}

/** The student bulk upload settings (PTEC-975), read and saved with the rest of the admin's. */
export interface StudentUploadSettings {
	studentUploadMaxRows: number;
	studentUploadPace: number;
	studentUploadMode: 'standard' | 'legacy';
}

export const MIN_SHARE_EXPIRY_DAYS = 1;
export const MAX_SHARE_EXPIRY_DAYS = 90;
export const DEFAULT_SHARE_EXPIRY_DAYS = 7;

interface PlatformSettingsState {
	showAdvertisement: boolean;
	glassEnabled: boolean;
	glassIntensity: number;
	fontFamily: FontFamilyKey;
	fontWeight: FontWeightKey;
	anaAvatarStyle: AnaAvatarStyle;
	anaFabStyle: AnaFabStyle;
	maxUploadSizeMb: number;
	deliveryPartnerNamespace: string;
	// Resolved server-side from the namespace; the share dialog compares document
	// provenance against it. Empty when unset or unresolvable, which makes the
	// eligibility guard fail closed.
	deliveryPartnerOrganisationId: string;
	deliveryPartners: DeliveryPartnerOption[];
	deliveryPartnersLoading: boolean;
	destinationCache: DestinationCacheStatus | null;
	destinationCacheLoading: boolean;
	destinationCacheRefreshing: boolean;
	// Populated when a manual refresh is rejected. The cache guards explain what
	// was wrong with ECE's response, which is the whole value of surfacing it.
	destinationCacheRefreshError: string | null;
	testDestination: TestDestination | null;
	shareExpiryDays: number;
	eceConfig: EceConfig | null;
	eceConfigLoading: boolean;
	eceConfigSaving: boolean;
	eceConnectionTest: EceConnectionTest | null;
	eceConnectionTesting: boolean;
	freeTier: FreeTierSettings;
	studentUpload: StudentUploadSettings | null;
	// The admin read failed, or answered without them: the panel says so rather than show
	// defaults that could then be saved over the real values.
	studentUploadUnavailable: boolean;
	studentUploadSaving: boolean;
	loading: boolean;
	loaded: boolean;
	saving: boolean;
}

const initialState: PlatformSettingsState = {
	showAdvertisement: false,
	glassEnabled: false,
	glassIntensity: 70,
	fontFamily: 'inter',
	fontWeight: 'regular',
	anaAvatarStyle: 'nebula',
	anaFabStyle: 'modern',
	maxUploadSizeMb: DEFAULT_MAX_UPLOAD_SIZE_MB,
	deliveryPartnerNamespace: '',
	deliveryPartnerOrganisationId: '',
	deliveryPartners: [],
	deliveryPartnersLoading: false,
	destinationCache: null,
	destinationCacheLoading: false,
	destinationCacheRefreshing: false,
	destinationCacheRefreshError: null,
	testDestination: null,
	shareExpiryDays: DEFAULT_SHARE_EXPIRY_DAYS,
	eceConfig: null,
	eceConfigLoading: false,
	eceConfigSaving: false,
	eceConnectionTest: null,
	eceConnectionTesting: false,
	freeTier: FREE_TIER_DEFAULTS,
	studentUpload: null,
	studentUploadUnavailable: false,
	studentUploadSaving: false,
	loading: false,
	loaded: false,
	saving: false
};

export const fetchPlatformSettings = createAsyncThunk('platformSettings/fetch', async (_, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', '/organisations/platform-settings', {});
		return response as {
			showAdvertisement: boolean;
			glassEnabled: boolean;
			glassIntensity: number;
			fontFamily?: FontFamilyKey;
			fontWeight?: FontWeightKey;
			anaAvatarStyle?: AnaAvatarStyle;
			anaFabStyle?: AnaFabStyle;
			maxUploadSizeMb?: number;
			deliveryPartnerNamespace?: string | null;
			deliveryPartnerOrganisationId?: string | null;
			freeTier?: FreeTierSettings;
		};
	} catch (error: unknown) {
		return rejectWithValue('Failed to fetch platform settings.');
	}
});

export const fetchDeliveryPartners = createAsyncThunk(
	'platformSettings/fetchDeliveryPartners',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.get('inspirusapi', '/admins/delivery-partners', {});
			return (response?.items || []) as DeliveryPartnerOption[];
		} catch (error: unknown) {
			return rejectWithValue('Failed to fetch delivery partners.');
		}
	}
);

export const fetchDestinationCacheStatus = createAsyncThunk(
	'platformSettings/fetchDestinationCacheStatus',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.get('inspirusapi', '/share-delivery/cache', {});
			return response as DestinationCacheStatus;
		} catch (error: unknown) {
			return rejectWithValue('Failed to read the destination cache status.');
		}
	}
);

// Read from the admin endpoint rather than the app-facing one: this is operator
// configuration and has no business reaching every user's store. One call for
// both values, since they live on the same record.
export const fetchAdminPlatformSettings = createAsyncThunk(
	'platformSettings/fetchAdminPlatformSettings',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.get('inspirusapi', '/admins/platform-settings', {});
			const studentUploadKnown =
				!response?.studentUploadSettingsUnavailable && response?.studentUploadMaxRows != null;
			return {
				testDestination: (response?.testDestination ?? null) as TestDestination | null,
				shareExpiryDays: Number(response?.shareExpiryDays) || DEFAULT_SHARE_EXPIRY_DAYS,
				studentUpload: studentUploadKnown
					? ({
							studentUploadMaxRows: response.studentUploadMaxRows,
							studentUploadPace: response.studentUploadPace,
							studentUploadMode: response.studentUploadMode
					  } as StudentUploadSettings)
					: null
			};
		} catch (error: unknown) {
			return rejectWithValue('Failed to read the admin platform settings.');
		}
	}
);

export const saveTestDestination = createAsyncThunk<TestDestination | null, TestDestination | null>(
	'platformSettings/saveTestDestination',
	async (testDestination, { rejectWithValue }) => {
		try {
			await API.put('inspirusapi', '/admins/platform-settings', { body: { testDestination } });
			return testDestination;
		} catch (error: unknown) {
			const message =
				(error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
				'Failed to save the test destination.';
			return rejectWithValue(message);
		}
	}
);

export const fetchEceConfig = createAsyncThunk('platformSettings/fetchEceConfig', async (_, { rejectWithValue }) => {
	try {
		return (await API.get('inspirusapi', '/share-delivery/config', {})) as EceConfig;
	} catch (error: unknown) {
		return rejectWithValue('Failed to read the ECE configuration.');
	}
});

export const saveEceConfig = createAsyncThunk<
	EceConfig,
	{
		baseUrl?: string;
		secret?: string;
		shareFile?: { retrievalPath?: string; metadataKey?: string; maxMb?: number; allowedHosts?: string[] };
	}
>('platformSettings/saveEceConfig', async (body, { rejectWithValue }) => {
	try {
		return (await API.put('inspirusapi', '/share-delivery/config', { body })) as EceConfig;
	} catch (error: unknown) {
		const message =
			(error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
			'Failed to update the ECE configuration.';
		return rejectWithValue(message);
	}
});

export const testEceConnection = createAsyncThunk(
	'platformSettings/testEceConnection',
	async (_, { rejectWithValue }) => {
		try {
			return (await API.post('inspirusapi', '/share-delivery/config/test', {})) as EceConnectionTest;
		} catch (error: unknown) {
			return rejectWithValue('Could not run the connection test.');
		}
	}
);

/** What one test fetch of a v2 returned. Never the bytes. */
export interface ShareFileTest {
	ok: boolean;
	reason?: string;
	status?: number | null;
	contentType?: string | null;
	bytes?: number;
	fetchMs?: number;
}

/** Fetches one v2 from ECE with the saved settings, without sharing anything (PTEC-896). */
export const testShareFileRetrieval = createAsyncThunk<ShareFileTest, { reference: string }>(
	'platformSettings/testShareFileRetrieval',
	async (body, { rejectWithValue }) => {
		try {
			return (await API.post('inspirusapi', '/share-delivery/config/test-share-file', { body })) as ShareFileTest;
		} catch (error: unknown) {
			return rejectWithValue('Could not run the retrieval test.');
		}
	}
);

export const refreshDestinationCache = createAsyncThunk(
	'platformSettings/refreshDestinationCache',
	async (_, { rejectWithValue }) => {
		try {
			const response = await API.post('inspirusapi', '/share-delivery/cache/refresh', {});
			return response as { fetchedAt: string; count: number; stale: boolean };
		} catch (error: unknown) {
			// The server's message names what was wrong with ECE's response, which is
			// the reason for surfacing this at all.
			const message =
				(error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
				'Failed to refresh the destination list.';
			return rejectWithValue(message);
		}
	}
);

export const savePlatformSettings = createAsyncThunk<
	void,
	Partial<
		Pick<
			PlatformSettingsState,
			| 'showAdvertisement'
			| 'glassEnabled'
			| 'glassIntensity'
			| 'fontFamily'
			| 'fontWeight'
			| 'anaAvatarStyle'
			| 'anaFabStyle'
			| 'maxUploadSizeMb'
			| 'deliveryPartnerNamespace'
			| 'shareExpiryDays'
		>
	>,
	{ state: RootState }
>('platformSettings/save', async (updates, { rejectWithValue }) => {
	try {
		await API.put('inspirusapi', '/admins/platform-settings', {
			body: updates
		});
	} catch (error: unknown) {
		return rejectWithValue('Failed to save platform settings.');
	}
});

/** Saves the student upload settings that changed, and returns them as the API now holds them. */
export const saveStudentUploadSettings = createAsyncThunk<StudentUploadSettings, Partial<StudentUploadSettings>>(
	'platformSettings/saveStudentUpload',
	async (changes, { rejectWithValue }) => {
		try {
			const response = await API.put('inspirusapi', '/admins/platform-settings', { body: changes });
			return {
				studentUploadMaxRows: response.studentUploadMaxRows,
				studentUploadPace: response.studentUploadPace,
				studentUploadMode: response.studentUploadMode
			};
		} catch (error: unknown) {
			return rejectWithValue('Failed to save the student upload settings.');
		}
	}
);

export const saveFreeTierSettings = createAsyncThunk<void, Partial<FreeTierSettings>, { state: RootState }>(
	'platformSettings/saveFreeTier',
	async (updates, { rejectWithValue }) => {
		try {
			await API.put('inspirusapi', '/admins/free-tier-settings', {
				body: updates
			});
		} catch (error: unknown) {
			return rejectWithValue('Failed to save free tier settings.');
		}
	}
);

const platformSettingsSlice = createSlice({
	name: 'platformSettings',
	initialState,
	reducers: {
		setShowAdvertisement: (state, action: PayloadAction<boolean>) => {
			state.showAdvertisement = action.payload;
		},
		setGlassEnabled: (state, action: PayloadAction<boolean>) => {
			state.glassEnabled = action.payload;
		},
		setGlassIntensity: (state, action: PayloadAction<number>) => {
			state.glassIntensity = action.payload;
		},
		setFontFamily: (state, action: PayloadAction<FontFamilyKey>) => {
			state.fontFamily = action.payload;
		},
		setFontWeight: (state, action: PayloadAction<FontWeightKey>) => {
			state.fontWeight = action.payload;
		},
		setAnaAvatarStyle: (state, action: PayloadAction<AnaAvatarStyle>) => {
			state.anaAvatarStyle = action.payload;
		},
		setAnaFabStyle: (state, action: PayloadAction<AnaFabStyle>) => {
			state.anaFabStyle = action.payload;
		},
		setMaxUploadSizeMb: (state, action: PayloadAction<number>) => {
			state.maxUploadSizeMb = action.payload;
		},
		setDeliveryPartnerNamespace: (state, action: PayloadAction<string>) => {
			state.deliveryPartnerNamespace = action.payload;
		},
		setShareExpiryDays: (state, action: PayloadAction<number>) => {
			state.shareExpiryDays = action.payload;
		},
		setFreeTier: (state, action: PayloadAction<Partial<FreeTierSettings>>) => {
			state.freeTier = { ...state.freeTier, ...action.payload };
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchPlatformSettings.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(fetchPlatformSettings.fulfilled, (state, action) => {
			state.loading = false;
			state.showAdvertisement = action.payload.showAdvertisement;
			state.glassEnabled = action.payload.glassEnabled;
			state.glassIntensity = action.payload.glassIntensity;
			if (action.payload.fontFamily) state.fontFamily = action.payload.fontFamily;
			if (action.payload.fontWeight) state.fontWeight = action.payload.fontWeight;
			if (action.payload.anaAvatarStyle) state.anaAvatarStyle = action.payload.anaAvatarStyle;
			if (action.payload.anaFabStyle) state.anaFabStyle = action.payload.anaFabStyle;
			if (typeof action.payload.maxUploadSizeMb === 'number') {
				state.maxUploadSizeMb = action.payload.maxUploadSizeMb;
			}
			// Null means unset or malformed on the server; the admin field shows empty.
			state.deliveryPartnerNamespace = action.payload.deliveryPartnerNamespace ?? '';
			state.deliveryPartnerOrganisationId = action.payload.deliveryPartnerOrganisationId ?? '';
			if (action.payload.freeTier) {
				state.freeTier = action.payload.freeTier;
			}
			state.loaded = true;
		});
		builder.addCase(fetchPlatformSettings.rejected, (state) => {
			state.loading = false;
			state.loaded = true;
		});
		builder.addCase(fetchDeliveryPartners.pending, (state) => {
			state.deliveryPartnersLoading = true;
		});
		builder.addCase(fetchDeliveryPartners.fulfilled, (state, action) => {
			state.deliveryPartnersLoading = false;
			state.deliveryPartners = action.payload;
		});
		builder.addCase(fetchDeliveryPartners.rejected, (state) => {
			state.deliveryPartnersLoading = false;
			state.deliveryPartners = [];
		});
		builder.addCase(fetchDestinationCacheStatus.pending, (state) => {
			state.destinationCacheLoading = true;
		});
		builder.addCase(fetchDestinationCacheStatus.fulfilled, (state, action) => {
			state.destinationCacheLoading = false;
			state.destinationCache = action.payload;
		});
		builder.addCase(fetchDestinationCacheStatus.rejected, (state) => {
			state.destinationCacheLoading = false;
			state.destinationCache = null;
		});
		builder.addCase(refreshDestinationCache.pending, (state) => {
			state.destinationCacheRefreshing = true;
			state.destinationCacheRefreshError = null;
		});
		builder.addCase(refreshDestinationCache.fulfilled, (state, action) => {
			state.destinationCacheRefreshing = false;
			state.destinationCache = {
				fetchedAt: action.payload.fetchedAt,
				count: action.payload.count,
				lastError: null,
				lastErrorAt: null
			};
		});
		builder.addCase(refreshDestinationCache.rejected, (state, action) => {
			state.destinationCacheRefreshing = false;
			state.destinationCacheRefreshError =
				typeof action.payload === 'string' ? action.payload : 'Failed to refresh the destination list.';
		});
		builder.addCase(saveTestDestination.fulfilled, (state, action) => {
			state.testDestination = action.payload;
		});
		builder.addCase(fetchAdminPlatformSettings.pending, (state) => {
			state.studentUploadUnavailable = false;
		});
		builder.addCase(fetchAdminPlatformSettings.fulfilled, (state, action) => {
			state.testDestination = action.payload.testDestination;
			state.shareExpiryDays = action.payload.shareExpiryDays;
			state.studentUpload = action.payload.studentUpload;
			state.studentUploadUnavailable = !action.payload.studentUpload;
		});
		builder.addCase(fetchAdminPlatformSettings.rejected, (state) => {
			state.studentUploadUnavailable = true;
		});
		builder.addCase(saveStudentUploadSettings.pending, (state) => {
			state.studentUploadSaving = true;
		});
		builder.addCase(saveStudentUploadSettings.fulfilled, (state, action) => {
			state.studentUploadSaving = false;
			state.studentUpload = action.payload;
		});
		builder.addCase(saveStudentUploadSettings.rejected, (state) => {
			state.studentUploadSaving = false;
		});
		builder.addCase(fetchEceConfig.pending, (state) => {
			state.eceConfigLoading = true;
		});
		builder.addCase(fetchEceConfig.fulfilled, (state, action) => {
			state.eceConfigLoading = false;
			state.eceConfig = action.payload;
		});
		builder.addCase(fetchEceConfig.rejected, (state) => {
			state.eceConfigLoading = false;
		});
		builder.addCase(saveEceConfig.pending, (state) => {
			state.eceConfigSaving = true;
		});
		builder.addCase(saveEceConfig.fulfilled, (state, action) => {
			state.eceConfigSaving = false;
			state.eceConfig = action.payload;
			// The previous result described the old credential.
			state.eceConnectionTest = null;
		});
		builder.addCase(saveEceConfig.rejected, (state) => {
			state.eceConfigSaving = false;
		});
		builder.addCase(testEceConnection.pending, (state) => {
			state.eceConnectionTesting = true;
			state.eceConnectionTest = null;
		});
		builder.addCase(testEceConnection.fulfilled, (state, action) => {
			state.eceConnectionTesting = false;
			state.eceConnectionTest = action.payload;
		});
		builder.addCase(testEceConnection.rejected, (state, action) => {
			state.eceConnectionTesting = false;
			state.eceConnectionTest = { ok: false, error: String(action.payload) };
		});
		// Save progress lives in the slice so pages don't track async state locally.
		builder.addCase(savePlatformSettings.pending, (state) => {
			state.saving = true;
		});
		builder.addCase(savePlatformSettings.fulfilled, (state) => {
			state.saving = false;
		});
		builder.addCase(savePlatformSettings.rejected, (state) => {
			state.saving = false;
		});
		builder.addCase(saveFreeTierSettings.pending, (state) => {
			state.saving = true;
		});
		builder.addCase(saveFreeTierSettings.fulfilled, (state) => {
			state.saving = false;
		});
		builder.addCase(saveFreeTierSettings.rejected, (state) => {
			state.saving = false;
		});
	}
});

export const {
	setShowAdvertisement,
	setGlassEnabled,
	setGlassIntensity,
	setFontFamily,
	setFontWeight,
	setAnaAvatarStyle,
	setAnaFabStyle,
	setMaxUploadSizeMb,
	setDeliveryPartnerNamespace,
	setShareExpiryDays,
	setFreeTier
} = platformSettingsSlice.actions;
export default platformSettingsSlice.reducer;
